import { DataFlowDataType, IDataFlowDataType, getDataFlowExpressionBuilderTypeIcon } from 'app/model/dataflow/dataflow-datatype';
import { DataFlowExpressionFunctionImpls } from './dataflow-expression-function-impls';
import { Expression } from './expressionTypes';

import { IErrorExpression } from './IErrorExpression';
import { DataFlowArgument, DataFlowFilterType } from './dataflow-argument';
import { functionsDescriptionMap } from './dataflow-expression-functions-map';
import { DataFlowTypesUtils } from './dataflow-type-utilities';

interface IDataFlowFunctionOutputRule {
    precisionAddition?: number;
    scaleAddition?: number;
    outputFilter?(inputArguments: Expression[], ...args: number[]): IDataFlowDataType;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    validateArgs?(inputArguments: Expression[], extraObject?: any): IErrorExpression[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    validateParent?(functionRef: Expression, parentContext: any): IErrorExpression[];
}

export interface IDataFlowFunctionData {
    description?: string;
    returnType: DataFlowDataType;
    examples: string[];
    parametersDescription: string[] ;
    parametersToAutoComplete: string[];
    inputTypes: DataFlowDataType[];
    requiredArguments: number;
    functionType: string;
}

export class DataFlowFunctionArgument extends DataFlowArgument {
    public readonly dataTypeStrings = new Map<string, string>([
        ['string', 'string'],
        ['integer', 'numeric_value'],
        ['boolean', 'condition'],
        ['date', 'date'],
        ['datetime', 'datetime'],
        ['timestamp', 'time_stamp'],
        ['any', 'expression'],
        ['short', 'numeric_value'],
        ['double', 'numeric_value'],
        ['float', 'numeric_value'],
        ['decimal', 'numeric_value'],
        ['number', 'numeric_value'],
        ['binary', 'binary_value'],
        ['array', 'array'],
        ['complex', 'complex']
    ]);

    public inputTypes: DataFlowDataType[] = [];
    public description: string = '';
    public examples: string[] = [];
    public parametersDescription: string[] = [];
    public parametersToAutoComplete: string[] = [];
    public synonyms: string[] = [];
    public requiredArguments: number;
    public functionType: string;
    public isUserDefinedFunction: boolean = false;

    constructor(name: string,
        filterType: DataFlowFilterType,
        public variableArguments: boolean = false,
        public rule: IDataFlowFunctionOutputRule = {},
        public funcData?: IDataFlowFunctionData) {
        super(name, 'any', filterType);
        this.isUserDefinedFunction = !!funcData;
        const functionData = funcData ? funcData : functionsDescriptionMap[name];
        if (functionData) {
            this.type = functionData.returnType;
            this.inputTypes = functionData.inputTypes;
            this.description = functionData.description;
            this.examples = functionData.examples;
            this.parametersDescription = functionData.parametersDescription;
            this.parametersToAutoComplete = functionData.parametersToAutoComplete;
            this.synonyms = functionData.synonyms;
            this.requiredArguments = functionData.requiredParameters;
            this.functionType = functionData.functionType;
        }
    }

    public get minimumRequiredNumberOfArguments(): number {
        return this.requiredArguments === undefined ? this.inputTypes.length : this.requiredArguments;
    }

    public get maximumNumberOfArguments(): number {
        return this.inputTypes.length;
    }

    public getInsertText(): string {
        const functionArguments: string[] = this.parametersToAutoComplete.map((argument, index) => this._decorateArgumentText(argument, true, index + 1));
        return functionArguments.length ? `${this.name}(${functionArguments.join(', ')})` : `${this.name}${this.getArguments(true)}`;
    }

    public getTypeIcon(): string {
        return `icon ${getDataFlowExpressionBuilderTypeIcon(this.type)}`;
    }

    public getParameterLabels(): string[] {
        if (!this.inputTypes.length) {
            return [];
        }
        if (!this.parametersDescription.length) {
            const argumentString = this.getArguments();
            return argumentString.substring(1, argumentString.length - 1).split(', ');
        }
        return this.parametersDescription;
    }

    public getParameterIcons(): string[] {
        return this.inputTypes.map((inputType) => `arg-icon ${getDataFlowExpressionBuilderTypeIcon(inputType)}`);
    }

    public getExpressionTooltip(): string {
        let examples = this.examples.length > 0 ? stringResources.DataFlowUseExpressionBuilderExamples : '';
        for (let i = 0; i < this.examples.length; i += 2) {
            examples = `${examples}\n${Math.round(i / 2) + 1}. ${this.examples[i]}${this.examples[i + 1] ? ' -> ' + this.examples[i + 1] : ''}`;
        }
        return `${this.description}\n\n${examples}`;
    }

    public evaluateType(inputArguments: Expression[]): IDataFlowDataType {
        if (this.rule && this.rule.outputFilter) {
            const returnType = (this.rule.precisionAddition && this.rule.scaleAddition)
                ? this.rule.outputFilter(inputArguments, this.rule.precisionAddition, this.rule.scaleAddition) : this.rule.outputFilter(inputArguments);
            if (returnType) {
                return returnType; // Otherwise use get wide type as default way.
            }
        }

        if (!['any', 'number', 'integral'].includes(this.type)) {
            return DataFlowTypesUtils.createType(this.type);
        }
        return DataFlowTypesUtils.getWidestType(inputArguments.map((expression) => expression.returnType));
    }

    public getArguments(generateSnippets: boolean = false): string {
        if (this.inputTypes.length === 0) {
            return '()';
        } else if (this.inputTypes.length === 1) {
            return `(${this._decorateArgumentText(this.dataTypeStrings.get(this.inputTypes[0]), generateSnippets, 1)})`;
        } else {
            let expressionLabel = '(';
            const counters: Map<DataFlowDataType, number> = new Map();
            const argumentStrings: string[] = [];
            this.inputTypes.forEach((inputType: DataFlowDataType, index: number) => {
                if (counters.has(inputType)) {
                    counters.set(inputType, counters.get(inputType) + 1);
                } else {
                    counters.set(inputType, 1);
                }
                const inputString = this.dataTypeStrings.get(inputType);
                const ordinal = counters.get(inputType) === 1 ? 'first' : counters.get(inputType) === 2 ? 'second' : 'third';
                argumentStrings.push(this._decorateArgumentText(`${ordinal}_${inputString}`, generateSnippets, index + 1));
            });
            expressionLabel += argumentStrings.join(', ');
            expressionLabel += ')';
            Array.from(counters.keys()).filter((dataType: DataFlowDataType) => counters.get(dataType) === 1).forEach((dataType: DataFlowDataType) => {
                const inputString = this.dataTypeStrings.get(dataType);
                expressionLabel = expressionLabel.replace('first_' + inputString, inputString);
            });

            return expressionLabel;
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public evaluate(...objects: any[]): any {
        const funcName = 'dataflow_' + this.name;

        if (this.isExecutable()) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return (DataFlowExpressionFunctionImpls as any)[funcName](...objects);
        }

        return undefined;
    }

    public isExecutable(): boolean {
        const funcName = 'dataflow_' + this.name;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return !!(DataFlowExpressionFunctionImpls as any)[funcName];
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public validate(functionRef: Expression, parentContext: any, extraObject?: any): IErrorExpression[] {
        const violations: IErrorExpression[] = [];
        if (!this.rule.validateArgs && !this.rule.validateParent) {
            return undefined;
        }
        if (this.rule.validateArgs) {
            violations.push(...(this.rule.validateArgs(functionRef.functionExpression.children, extraObject) || []));
        }
        if (this.rule.validateParent) {
            violations.push(...(this.rule.validateParent(functionRef, parentContext) || []));
        }
        return violations;
    }

    private _decorateArgumentText(argumentText: string, generateSnippets: boolean, position: number): string {
        return generateSnippets ? `$\{${position}:${argumentText}}` : argumentText;
    }
}
